import { Component, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  RouteConfigLoadStart,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import Intercom from '@intercom/messenger-js-sdk';

import { AppActivatedRouteSnapshot } from '@app/app-routing.interface';
import { UserState } from '@app/auth/user.state';
import { FooterComponent } from '@app/footer/footer.component';
import { LayoutService } from '@app/layout/layout.service';
import { SidebarComponent } from '@app/layout/sidebar/sidebar.component';
import { MainNavComponent } from '@app/main-nav/main-nav.component';
import { MessageService } from '@app/messages/message.service';
import { MessagesComponent } from '@app/messages/messages.component';
import { Category, EventType } from '@app/shared/data-layer/data-layer.model';
import { DataLayerService } from '@app/shared/data-layer/data-layer.service';
import { InactivityDetectionService } from '@app/shared/inactivity-detection.service';
import { ModalService } from '@app/shared/modal/modal.service';
import { OffcanvasService } from '@app/shared/offcanvas/offcanvas.service';
import { SharedService } from '@app/shared/shared.service';
import { TitleService } from '@app/shared/title.service';

@Component({
  imports: [
    FooterComponent,
    MainNavComponent,
    MessagesComponent,
    RouterModule,
    RouterOutlet,
    SidebarComponent,
  ],
  providers: [LayoutService],
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      #main {
        flex: 1 1 auto;
        flex-direction: column;
        display: flex;
        justify-content: flex-start;
      }
    `,
  ],
  template: `
    <div class="d-flex flex-grow-1 overflow-hidden">
      <div class="sidebar col-auto d-flex bg-white border-end p-2 shadow-sm">
        <app-sidebar></app-sidebar>
      </div>
      <div class="container-fluid p-0 col d-flex flex-column overflow-auto">
        <header>
          <app-main-nav></app-main-nav>
        </header>
        <div id="main-container" class="overflow-auto d-flex flex-column flex-grow-1">
          <app-messages></app-messages>
          <main id="main" class="position-relative">
            <router-outlet></router-outlet>
          </main>
          <app-footer />
        </div>
      </div>
    </div>
  `,
})
export class AppFrameComponent {
  private messageService = inject(MessageService);
  private router = inject(Router);
  private sharedService = inject(SharedService);
  private titleService = inject(TitleService);
  private userState = inject(UserState);

  // These two services are needed for route based modals/offcanvases to work.
  private _modalService = inject(ModalService);
  private _offcanvasService = inject(OffcanvasService);

  // This needs to be included here to activate inactivity detection on app load.
  private readonly _inactivityDetectionService = inject(InactivityDetectionService);

  private dataLayerService = inject(DataLayerService);
  private routeEvents = toSignal(this.router.events);

  // Track lazy loading state with signal
  private isLazyLoading = signal(false);

  constructor() {
    effect(() => {
      const userId = this.userState.userId();
      if (userId < 1) {
        return;
      }
      untracked(() => {
        const user = this.userState.user();

        if (!user) {
          return;
        }

        this.dataLayerService.logEvent({
          category: Category.User,
          event: EventType.Login,
          loginProvider: user.provider || '',
          userId: user.id,
        });

        Intercom({
          app_id: 'oa17dxqn',
          email: user.email,
          hide_default_launcher: true,
          user_id: `${user.id}`,
        });
      });
    });

    effect(() => {
      const event = this.routeEvents();
      untracked(() => {
        // RouteConfigLoadStart can fire multiple times (e.g., nested lazy routes),
        // so we use an intermediate signal to track status and prevent the
        // MessageService's internal counter from going out of sync.
        if (event instanceof RouteConfigLoadStart) {
          this.isLazyLoading.set(true);
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.isLazyLoading.set(false);
        }
      });
    });

    effect(() => {
      const isLoading = this.isLazyLoading();
      untracked(() => {
        this.messageService.showLoading = isLoading;
      });
    });

    effect(() => {
      const event = this.routeEvents();
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      untracked(() => {
        const routes = this.getRoutes(this.router.routerState.root.snapshot.firstChild);
        this.titleService.setTitleFromRoutes(routes);
        this.sharedService.routeData.next(routes[0]?.routeConfig?.data);
      });
    });
  }

  /**
   * Returns all routes from children to root.
   * @param routeSnapshot
   * @param routes
   * @private
   */
  private getRoutes(
    routeSnapshot: null | ActivatedRouteSnapshot,
    routes: AppActivatedRouteSnapshot[] = [],
  ): AppActivatedRouteSnapshot[] {
    if (routeSnapshot === null) {
      return [];
    }
    const firstElement = routeSnapshot.firstChild;
    if (firstElement) {
      this.getRoutes(firstElement, routes);
    }
    routes.push(routeSnapshot as AppActivatedRouteSnapshot);
    return routes;
  }
}
